import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, 
  Mic, 
  MicOff,
  Plane, 
  TrendingUp, 
  Palette, 
  ListTodo, 
  Heart,
  ArrowRight,
  Sparkles,
  Brain,
  Zap,
  Search,
  Image,
  Wand2,
  Newspaper,
  Upload,
  Send,
  Loader2,
  X,
  ExternalLink,
  Building2,
  Palmtree,
  Car,
  Shield,
  MapPin
} from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import { AIBrainVisualizer } from '../components/ui/AIVoiceVisualizer';
import FilmStudioSection from '../components/FilmStudioSection';
import NewsSection from '../components/news/NewsSection';
import { Footer } from '../components/layout/Footer';
import { toast } from 'sonner';

const LOGO_URL = '/aira-echo-logo.png';

// Premium travel imagery for magazine-style collage
const travelImages = {
  airplane: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=90&fit=crop',
  varanasi: 'https://images.unsplash.com/photo-1627894483216-2138af692e32?w=800&q=90&fit=crop',
  maldives: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=90&fit=crop',
  hotel: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=90&fit=crop',
  london: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800&q=90&fit=crop',
};

// Travel service cards for landing page
const landingTravelServices = [
  { id: 'flights', icon: Plane, label: 'Flights', gradient: 'from-rose-500 to-pink-600', route: '/travel' },
  { id: 'hotels', icon: Building2, label: 'Hotels', gradient: 'from-purple-500 to-indigo-600', route: '/stays' },
  { id: 'holidays', icon: Palmtree, label: 'Holidays', gradient: 'from-amber-500 to-orange-600', route: '/tours' },
  { id: 'cabs', icon: Car, label: 'Cabs', gradient: 'from-cyan-500 to-teal-600', route: '/cabs' },
  { id: 'insurance', icon: Shield, label: 'Insurance', gradient: 'from-emerald-500 to-green-600', route: '/insurance' },
  { id: 'planner', icon: MapPin, label: 'AI Planner', gradient: 'from-violet-500 to-purple-600', route: '/trip-planner' },
];

// Magazine-Style Travel Hero Section for Landing Page
const TravelHeroSection = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const recognitionRef = useRef(null);

  const startListening = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Speech recognition not supported');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'en-US';
    recognitionRef.current.onstart = () => setIsListening(true);
    recognitionRef.current.onresult = (event) => {
      const transcript = Array.from(event.results).map(r => r[0].transcript).join('');
      setQuery(transcript);
    };
    recognitionRef.current.onend = () => setIsListening(false);
    recognitionRef.current.onerror = () => setIsListening(false);
    recognitionRef.current.start();
  }, []);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setIsListening(false);
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setIsProcessing(true);
    setTimeout(() => navigate(`/trip-planner?q=${encodeURIComponent(query)}`), 500);
  };

  return (
    <section className="relative z-10 pt-6 pb-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Magazine-Style Travel Collage Hero */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative w-full h-[360px] sm:h-[420px] lg:h-[480px] rounded-3xl overflow-hidden mb-10"
          style={{ boxShadow: '0 0 100px rgba(245, 158, 11, 0.15), 0 0 60px rgba(168, 85, 247, 0.1), 0 30px 80px rgba(0,0,0,0.6)' }}
        >
          {/* Premium outer glow */}
          <div className="absolute -inset-[2px] rounded-3xl pointer-events-none" style={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.4), rgba(168, 85, 247, 0.3), rgba(6, 182, 212, 0.3))',
            filter: 'blur(3px)', opacity: 0.6
          }} />

          {/* Image collage grid */}
          <div className="absolute inset-0 rounded-3xl overflow-hidden border border-white/10">
            <div className="absolute inset-0 grid grid-cols-4 grid-rows-2 gap-[2px]">
              <motion.div className="col-span-2 row-span-2 relative overflow-hidden" initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2 }}>
                <img src={travelImages.airplane} alt="Flight" className="w-full h-full object-cover" style={{ filter: 'brightness(0.85) saturate(1.1)' }} />
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/15 via-transparent to-purple-900/40" />
              </motion.div>
              <motion.div className="relative overflow-hidden" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
                <img src={travelImages.maldives} alt="Maldives" className="w-full h-full object-cover" style={{ filter: 'brightness(0.95) saturate(1.15)' }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </motion.div>
              <motion.div className="relative overflow-hidden" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}>
                <img src={travelImages.hotel} alt="Hotel" className="w-full h-full object-cover" style={{ filter: 'brightness(0.9)' }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </motion.div>
              <motion.div className="relative overflow-hidden" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}>
                <img src={travelImages.varanasi} alt="Varanasi" className="w-full h-full object-cover" style={{ filter: 'brightness(1.05) saturate(1.2)' }} />
                <div className="absolute inset-0 bg-gradient-to-t from-amber-900/50 via-amber-500/10 to-transparent" />
              </motion.div>
              <motion.div className="relative overflow-hidden" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}>
                <img src={travelImages.london} alt="London" className="w-full h-full object-cover" style={{ filter: 'brightness(0.85)' }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </motion.div>
            </div>

            {/* Dark overlay */}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.25) 60%, rgba(0,0,0,0.8) 100%)' }} />

            {/* Accent lines */}
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(6, 182, 212, 0.6) 20%, rgba(168, 85, 247, 0.6) 80%, transparent 100%)', boxShadow: '0 0 20px rgba(6, 182, 212, 0.5)' }} />
            <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(245, 158, 11, 0.6) 30%, rgba(236, 72, 153, 0.6) 70%, transparent 100%)', boxShadow: '0 0 15px rgba(245, 158, 11, 0.4)' }} />
            <div className="absolute top-0 left-0 w-48 h-48" style={{ background: 'radial-gradient(circle at 0% 0%, rgba(168, 85, 247, 0.25) 0%, transparent 60%)' }} />
            <div className="absolute bottom-0 right-0 w-56 h-56" style={{ background: 'radial-gradient(circle at 100% 100%, rgba(245, 158, 11, 0.2) 0%, transparent 60%)' }} />

            {/* Content */}
            <div className="absolute inset-0 flex flex-col justify-center items-center text-center p-6">
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-4">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-white/90">AI-Powered Travel Planning</span>
              </motion.div>

              <motion.h2 initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 0.8 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-white mb-3"
                style={{ fontFamily: '"Playfair Display", Georgia, serif', textShadow: '0 0 60px rgba(245, 158, 11, 0.4), 0 4px 30px rgba(0,0,0,0.9)', letterSpacing: '0.03em' }}
              >
                Discover the World with
                <br />
                <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-pink-400 bg-clip-text text-transparent">Intelligence & Sophistication</span>
              </motion.h2>

              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="text-white/60 text-sm sm:text-base tracking-wide max-w-2xl mb-6" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
                Flights • Hotels • Tours • Cabs • Insurance • Complete Holiday Planning
              </motion.p>

              {/* AI Search Bar */}
              <motion.form onSubmit={handleSubmit} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }} className="w-full max-w-2xl">
                <div className="relative flex items-center bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-1.5 hover:border-white/30 transition-all" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)' }}>
                  <div className="flex-1 flex items-center gap-3 px-4">
                    <MapPin className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ask AI: Plan my trip to Maldives for 5 days..." className="w-full bg-transparent text-white placeholder-white/40 text-base py-3 focus:outline-none" />
                  </div>
                  <div className="flex items-center gap-2 pr-2">
                    <button type="button" onClick={isListening ? stopListening : startListening} className={`p-2.5 rounded-xl transition-all ${isListening ? 'bg-red-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'}`}>
                      {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                    </button>
                    <button type="submit" disabled={isProcessing || !query.trim()} className="p-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white disabled:opacity-50 transition-all hover:shadow-lg hover:shadow-amber-500/25">
                      {isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </motion.form>
            </div>
          </div>
        </motion.div>

        {/* Service Cards Grid */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}>
          <div className="flex items-center justify-center gap-2 mb-6">
            <Sparkles className="w-5 h-5 text-amber-400" style={{ filter: 'drop-shadow(0 0 8px rgba(245, 158, 11, 0.5))' }} />
            <h3 className="text-white font-medium text-lg">Plan Your Complete Journey</h3>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 max-w-4xl mx-auto">
            {landingTravelServices.map((service, idx) => (
              <motion.button key={service.id} onClick={() => navigate(service.route)} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 + idx * 0.05 }} whileHover={{ scale: 1.05, y: -5 }} whileTap={{ scale: 0.98 }}
                className="flex flex-col items-center gap-3 p-4 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-white/25 hover:bg-white/10 transition-all group" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center group-hover:shadow-lg transition-all`}>
                  <service.icon className="w-6 h-6 text-white" />
                </div>
                <span className="text-white/80 text-sm font-medium group-hover:text-white transition-colors">{service.label}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Feature hints */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-10 text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Smart Search</span>
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-purple-500" />AI Recommendations</span>
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />Price Comparison</span>
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-pink-500" />Instant Booking</span>
        </motion.div>
      </div>
    </section>
  );
};

// Premium Splash Screen Component - Netflix Style
const SplashScreen = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 3200);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="fixed inset-0 z-[100] bg-[#000000] flex items-center justify-center overflow-hidden"
    >
      {/* Cinematic Background */}
      <div className="absolute inset-0">
        {/* Deep space gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0015] via-[#000000] to-[#0a0015]" />
        
        {/* Animated glow orbs - subtle */}
        <motion.div
          initial={{ opacity: 0, scale: 0.3 }}
          animate={{ opacity: 0.35, scale: 1 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px]"
          style={{
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.06) 30%, transparent 70%)',
            filter: 'blur(50px)'
          }}
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px]"
          style={{
            background: 'radial-gradient(circle, rgba(236, 72, 153, 0.12) 0%, transparent 60%)',
            filter: 'blur(35px)'
          }}
        />
      </div>

      {/* Main Logo Container */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="relative z-10 flex flex-col items-center"
      >
        {/* Premium Logo with Netflix-style reveal */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ 
            duration: 0.8, 
            ease: [0.34, 1.56, 0.64, 1], // Bounce effect
            delay: 0.2
          }}
          className="relative"
        >
          {/* Outer glow ring */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: [0, 0.5, 0.25], scale: [0.8, 1.1, 1] }}
            transition={{ duration: 1.5, delay: 0.3 }}
            className="absolute -inset-8 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(99, 102, 241, 0.25) 0%, transparent 70%)',
              filter: 'blur(25px)'
            }}
          />
          
          {/* Logo Image - Large & Premium */}
          <motion.img 
            src={LOGO_URL} 
            alt="AIRA ECHO" 
            className="w-56 h-56 sm:w-72 sm:h-72 md:w-80 md:h-80 lg:w-96 lg:h-96 relative z-10"
            initial={{ filter: 'brightness(0)' }}
            animate={{ filter: 'brightness(1)' }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{
              filter: 'drop-shadow(0 0 30px rgba(99, 102, 241, 0.4)) drop-shadow(0 0 60px rgba(139, 92, 246, 0.25)) drop-shadow(0 0 90px rgba(236, 72, 153, 0.15))'
            }}
          />
        </motion.div>
        
        {/* Premium AIRA ECHO Text - Netflix Style */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6, ease: 'easeOut' }}
          className="mt-8 flex flex-col items-center"
        >
          <h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight relative"
            style={{
              fontFamily: '"Inter", "SF Pro Display", -apple-system, sans-serif',
              letterSpacing: '-0.03em'
            }}
          >
            <span 
              style={{
                background: 'linear-gradient(180deg, #818cf8 0%, #6366f1 40%, #4f46e5 70%, #3730a3 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 2px 15px rgba(99, 102, 241, 0.4)) drop-shadow(0 4px 30px rgba(139, 92, 246, 0.25))'
              }}
            >
              AIRA
            </span>
            {' '}
            <span 
              style={{
                background: 'linear-gradient(180deg, #ffffff 0%, #e2e8f0 50%, #94a3b8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 2px 10px rgba(255, 255, 255, 0.15))'
              }}
            >
              ECHO
            </span>
          </h1>
          
          {/* Animated underline */}
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: '100%', opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.6, ease: 'easeOut' }}
            className="mt-3 h-[2px] rounded-full"
            style={{
              background: 'linear-gradient(90deg, transparent, #6366f1, #8b5cf6, #6366f1, transparent)',
              boxShadow: '0 0 10px rgba(99, 102, 241, 0.4), 0 0 20px rgba(139, 92, 246, 0.25)'
            }}
          />
        </motion.div>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.5 }}
          className="mt-6 text-white/40 text-sm sm:text-base md:text-lg tracking-[0.5em] uppercase font-light"
          style={{ fontFamily: '"Inter", sans-serif' }}
        >
          Your AI Brain
        </motion.p>

        {/* Premium Loading bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.3 }}
          className="mt-10 h-[2px] bg-white/5 rounded-full overflow-hidden w-48 sm:w-64"
          style={{ boxShadow: 'inset 0 0 8px rgba(0,0,0,0.4)' }}
        >
          <motion.div
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 1.6, delay: 1.5, ease: [0.4, 0, 0.2, 1] }}
            className="h-full rounded-full relative overflow-hidden"
            style={{
              background: 'linear-gradient(90deg, #4f46e5, #6366f1, #8b5cf6, #a855f7, #ec4899)',
              boxShadow: '0 0 15px rgba(99, 102, 241, 0.5), 0 0 30px rgba(139, 92, 246, 0.3)'
            }}
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

const features = [
  { icon: MessageSquare, title: 'AIRA Chat', desc: 'AI conversations, research, writing & coding help' },
  { icon: Mic, title: 'Voice Agent', desc: 'Natural voice conversations with AI' },
  { icon: Plane, title: 'Travel & Stay', desc: 'Flight search, hotels & itinerary planning' },
  { icon: TrendingUp, title: 'Investment Guru', desc: 'Financial guidance, expense tracking & calculators' },
  { icon: Palette, title: 'Creative Studio', desc: 'Design tools, photo editing & brand creation' },
  { icon: ListTodo, title: 'Life Manager', desc: 'Tasks, goals, habits & productivity insights' },
  { icon: Heart, title: 'Life Coach', desc: 'Emotional support & personal growth guidance' },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

// Sample news data (would come from API in production)
const sampleNews = [
  {
    id: 1,
    title: "AI Revolution: How Machine Learning is Transforming Industries",
    summary: "Discover how artificial intelligence is reshaping business landscapes worldwide.",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&q=80",
    source: "Tech Weekly",
    url: "#"
  },
  {
    id: 2,
    title: "Global Markets Reach New Heights Amid Tech Surge",
    summary: "Stock markets see unprecedented growth driven by AI and tech innovations.",
    image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&q=80",
    source: "Financial Times",
    url: "#"
  },
  {
    id: 3,
    title: "Breakthrough in Sustainable Energy Storage",
    summary: "Scientists announce major advancement in battery technology for renewable energy.",
    image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=400&q=80",
    source: "Science Daily",
    url: "#"
  },
  {
    id: 4,
    title: "Remote Work Transforms Urban Living",
    summary: "How the shift to remote work is changing where and how people live.",
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&q=80",
    source: "Business Insider",
    url: "#"
  }
];

// Universal AI Search Component
const UniversalAISearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activePanel, setActivePanel] = useState(null); // 'image', 'edit', 'news', 'response'
  const [aiResponse, setAiResponse] = useState(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [uploadedImage, setUploadedImage] = useState(null);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Initialize speech recognition
  const startListening = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'en-US';

    recognitionRef.current.onstart = () => {
      setIsListening(true);
    };

    recognitionRef.current.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(result => result[0].transcript)
        .join('');
      setQuery(transcript);
    };

    recognitionRef.current.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognitionRef.current.start();
  }, []);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  }, []);

  // Handle search/query submission
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;

    // Detect intent and route accordingly
    const lowerQuery = query.toLowerCase();
    
    // Check for image generation prompts using enhanced detection
    const imageGenerationTriggers = [
      'create an image', 'generate an image', 'make an image', 'draw',
      'create a picture', 'generate a picture', 'make a picture',
      'create an illustration', 'generate an illustration', 'make an illustration',
      'create a poster', 'generate a poster', 'make a poster',
      'create artwork', 'generate artwork', 'design an image',
      'paint', 'visualize', 'render an image', 'create a graphic',
      'generate a graphic', 'sketch', 'create a photo', 'generate a photo',
      'create a portrait', 'make a logo', 'design a logo', 'create a banner'
    ];
    
    const isImageRequest = imageGenerationTriggers.some(trigger => lowerQuery.includes(trigger));
    
    if (isImageRequest) {
      handleImageGeneration(query);
      return;
    }
    
    if (lowerQuery.includes('business idea') || lowerQuery.includes('startup')) {
      navigate('/login?redirect=/business');
      return;
    }
    if (lowerQuery.includes('travel') || lowerQuery.includes('flight') || lowerQuery.includes('hotel')) {
      navigate('/login?redirect=/travel');
      return;
    }
    if (lowerQuery.includes('invest') || lowerQuery.includes('stock') || lowerQuery.includes('finance')) {
      navigate('/login?redirect=/investment');
      return;
    }
    if (lowerQuery.includes('news') || lowerQuery.includes('headlines')) {
      setActivePanel('news');
      return;
    }

    // Default: Show AI response with streaming - use public streaming endpoint
    setIsProcessing(true);
    setActivePanel('response');
    setAiResponse({ text: '', limited: true, isStreaming: true, suggestions: [] });
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/public/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: query })
      });
      
      if (!response.ok) throw new Error('Stream failed');
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.chunk) {
                fullResponse += data.chunk;
                setAiResponse(prev => ({
                  ...prev,
                  text: fullResponse
                }));
              }
              
              if (data.done) {
                setAiResponse(prev => ({
                  ...prev,
                  isStreaming: false,
                  limited: data.limited,
                  suggestions: [
                    'Sign up for full access',
                    'Explore AIRA features',
                    'Try voice conversation'
                  ]
                }));
                setIsProcessing(false);
              }
              
              if (data.error) {
                throw new Error(data.error);
              }
            } catch (parseError) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }
    } catch (error) {
      console.error('Public chat error:', error);
      setAiResponse({
        text: `I'd love to help you with "${query}"! Sign up for AIRA ECHO to unlock unlimited AI conversations, voice chat, image generation, and much more!`,
        isStreaming: false,
        suggestions: [
          'Sign up now',
          'Explore features',
          'Try voice chat'
        ]
      });
      setIsProcessing(false);
    }
  };

  // Handle image generation using Nano Banana
  const handleImageGeneration = async (prompt) => {
    setIsProcessing(true);
    setActivePanel('image');
    setGeneratedImages([]);
    
    try {
      // Call the public image generation endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/image/generate/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: prompt })
      });
      
      if (!response.ok) {
        throw new Error('Image generation failed');
      }
      
      const data = await response.json();
      
      if (data.success && data.images && data.images.length > 0) {
        // Convert base64 images to data URLs
        const imageUrls = data.images.map(img => 
          `data:${img.mime_type || 'image/png'};base64,${img.data}`
        );
        setGeneratedImages(imageUrls);
        toast.success('Image generated with Nano Banana! Sign up for more.');
      } else {
        throw new Error(data.error || 'No images generated');
      }
    } catch (error) {
      console.error('Image generation error:', error);
      // Fallback to placeholder images if API fails
      setGeneratedImages([
        'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=400&q=80',
        'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&q=80'
      ]);
      toast.info('Preview images shown. Sign up for AI-powered generation!');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle image upload for editing
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target.result);
        setActivePanel('edit');
        toast.info('Image uploaded! Sign up to access AI editing tools.');
      };
      reader.readAsDataURL(file);
    }
  };

  const closePanel = () => {
    setActivePanel(null);
    setAiResponse(null);
    setGeneratedImages([]);
    setUploadedImage(null);
  };

  const AIRA_LOGO = '/aira-echo-logo.png';

  return (
    <section className="relative z-10 py-16">
      <div className="max-w-5xl mx-auto px-6">
        {/* Section Title with Logo on Right */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-center justify-center gap-3 mb-8"
        >
          {/* Title */}
          <div className="text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-1 relative inline-block">
              <span className="glow-text">AIRA </span>
              <span className="text-white">ECHO</span>
              {/* Neon Underline */}
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-[2px] rounded-full"
                style={{
                  background: 'linear-gradient(90deg, transparent, #00f0ff, #7c3aed, #00f0ff, transparent)',
                  backgroundSize: '200% 100%',
                }}
                animate={{
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                  boxShadow: [
                    '0 0 8px rgba(0, 240, 255, 0.5), 0 0 16px rgba(0, 240, 255, 0.3)',
                    '0 0 12px rgba(124, 58, 237, 0.6), 0 0 24px rgba(124, 58, 237, 0.4)',
                    '0 0 8px rgba(0, 240, 255, 0.5), 0 0 16px rgba(0, 240, 255, 0.3)',
                  ],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear',
                }}
              />
            </h2>
            <p className="text-gray-400 text-sm mt-3">
              Your universal gateway to AI-powered search, chat, and creation
            </p>
          </div>
          
          {/* AIRA Logo - Subtle on Right */}
          <motion.img
            src={AIRA_LOGO}
            alt="AIRA ECHO"
            className="w-10 h-10 md:w-12 md:h-12 object-contain opacity-70 hover:opacity-100 transition-opacity"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 0.7, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
          />
        </motion.div>

        {/* Main Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          <form onSubmit={handleSubmit} className="relative">
            <div className="relative group">
              {/* Glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-primary/50 via-secondary/50 to-accent/50 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
              
              {/* Search container */}
              <div className="relative flex items-center gap-3 bg-[#0a0a1a]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-2 pl-5 shadow-2xl">
                <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
                
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ask AIRA ECHO anything... Search, create, explore..."
                  className="flex-1 bg-transparent border-none outline-none text-white placeholder-gray-500 text-base py-3"
                  data-testid="universal-search-input"
                />

                {/* Voice button */}
                <motion.button
                  type="button"
                  onClick={isListening ? stopListening : startListening}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`p-3 rounded-xl transition-all ${
                    isListening 
                      ? 'bg-red-500/20 text-red-400 animate-pulse' 
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid="voice-search-btn"
                >
                  {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </motion.button>

                {/* Upload button */}
                <motion.button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-3 rounded-xl bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white transition-all"
                  data-testid="upload-btn"
                >
                  <Upload className="w-5 h-5" />
                </motion.button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />

                {/* Submit button */}
                <motion.button
                  type="submit"
                  disabled={isProcessing || !query.trim()}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-3 rounded-xl bg-gradient-to-r from-primary to-secondary text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  data-testid="search-submit-btn"
                >
                  {isProcessing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </motion.button>
              </div>
            </div>
          </form>

          {/* Voice listening indicator */}
          <AnimatePresence>
            {isListening && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center justify-center gap-2 mt-4 text-accent"
              >
                <div className="flex gap-1">
                  {[...Array(4)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-1 bg-accent rounded-full"
                      animate={{ height: [8, 20, 8] }}
                      transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.1 }}
                    />
                  ))}
                </div>
                <span className="text-sm">Listening... Speak now</span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Quick Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap justify-center gap-3 mt-6"
        >
          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setQuery('Create an image of ');
              setActivePanel('image');
              handleImageGeneration('futuristic city');
            }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-300 hover:border-purple-400/50 transition-all"
            data-testid="create-image-btn"
          >
            <Image className="w-4 h-4" />
            <span className="text-sm font-medium">Create Image</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-cyan-300 hover:border-cyan-400/50 transition-all"
            data-testid="edit-image-btn"
          >
            <Wand2 className="w-4 h-4" />
            <span className="text-sm font-medium">Edit Image</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActivePanel('news')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 text-green-300 hover:border-green-400/50 transition-all"
            data-testid="latest-news-btn"
          >
            <Newspaper className="w-4 h-4" />
            <span className="text-sm font-medium">Latest News</span>
          </motion.button>
        </motion.div>

        {/* Expandable Panels */}
        <AnimatePresence mode="wait">
          {activePanel && (
            <motion.div
              key={activePanel}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-8 overflow-hidden"
            >
              <GlassCard className="p-6 relative">
                {/* Close button */}
                <button
                  onClick={closePanel}
                  className="absolute top-4 right-4 p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all"
                >
                  <X className="w-4 h-4" />
                </button>

                {/* AI Response Panel */}
                {activePanel === 'response' && (
                  <div className="pr-8">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center flex-shrink-0">
                        <Brain className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-white mb-2">AIRA Response</h4>
                        {isProcessing && !aiResponse?.text ? (
                          <div className="flex items-center gap-2 text-gray-400">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Thinking...</span>
                          </div>
                        ) : (
                          <>
                            <p className="text-gray-300 mb-4 whitespace-pre-wrap">
                              {aiResponse?.text}
                              {aiResponse?.isStreaming && (
                                <span className="inline-block w-2 h-5 ml-1 bg-primary animate-pulse rounded-sm" />
                              )}
                            </p>
                            {!aiResponse?.isStreaming && aiResponse?.suggestions && (
                              <div className="flex flex-wrap gap-2">
                                {aiResponse?.suggestions?.map((suggestion, idx) => (
                                  <Link
                                    key={idx}
                                    to="/signup"
                                    className="px-3 py-1.5 rounded-full bg-primary/20 text-primary text-sm hover:bg-primary/30 transition-all"
                                  >
                                    {suggestion}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Image Generation Panel */}
                {activePanel === 'image' && (
                  <div className="pr-8">
                    <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <Image className="w-5 h-5 text-purple-400" />
                      Generated Images
                    </h4>
                    {isProcessing ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
                          <p className="text-gray-400">Generating images...</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          {generatedImages.map((img, idx) => (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: idx * 0.1 }}
                              className="relative group aspect-square rounded-xl overflow-hidden"
                            >
                              <img src={img} alt={`Generated ${idx + 1}`} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-3">
                                <span className="text-xs text-white">Click to expand</span>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                        <div className="flex justify-center">
                          <Link
                            to="/creative"
                            className="btn-primary text-sm flex items-center gap-2"
                          >
                            Create custom images
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Image Edit Panel */}
                {activePanel === 'edit' && uploadedImage && (
                  <div className="pr-8">
                    <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <Wand2 className="w-5 h-5 text-cyan-400" />
                      Edit Your Image
                    </h4>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="aspect-video rounded-xl overflow-hidden bg-black/20">
                        <img src={uploadedImage} alt="Uploaded" className="w-full h-full object-contain" />
                      </div>
                      <div className="space-y-3">
                        <h5 className="text-sm text-gray-400 mb-2">Available AI Tools:</h5>
                        {['Remove Background', 'Style Transfer', 'Enhance Quality', 'Change Colors'].map((tool, idx) => (
                          <Link
                            key={idx}
                            to="/creative"
                            className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all group"
                          >
                            <span className="text-gray-300">{tool}</span>
                            <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-primary transition-colors" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* News Panel */}
                {activePanel === 'news' && (
                  <div className="pr-8">
                    <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                      <Newspaper className="w-5 h-5 text-green-400" />
                      Latest News
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {sampleNews.map((news, idx) => (
                        <motion.a
                          key={news.id}
                          href={news.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="flex gap-4 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/10 transition-all group"
                        >
                          <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                            <img src={news.image} alt={news.title} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-accent mb-1 block">{news.source}</span>
                            <h5 className="text-sm font-medium text-white line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                              {news.title}
                            </h5>
                            <p className="text-xs text-gray-500 line-clamp-2">{news.summary}</p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-500 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </motion.a>
                      ))}
                    </div>
                    <div className="flex justify-center mt-4">
                      <Link
                        to="/dashboard"
                        className="text-sm text-primary hover:text-accent transition-colors flex items-center gap-1"
                      >
                        Explore personalized news feed
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                )}
              </GlassCard>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Feature hints */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-6 text-xs text-gray-500"
        >
          <span className="flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-primary" />
            AI Chat
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-secondary" />
            Web Search
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-accent" />
            Image Generation
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-purple-500" />
            Voice Chat
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-green-500" />
            News Search
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default function LandingPage() {
  const [showSplash, setShowSplash] = useState(true); // Splash enabled

  return (
    <>
      {/* Premium Splash Screen */}
      <AnimatePresence>
        {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
      </AnimatePresence>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: showSplash ? 0 : 1 }}
        transition={{ duration: 0.3 }}
        className="min-h-screen bg-[#030014] overflow-hidden"
      >
      {/* Background Effects - Warm amber & purple for travel theme */}
      <div className="fixed inset-0 pointer-events-none" style={{ willChange: 'auto' }}>
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-amber-500/6 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-purple-600/8 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-400/4 rounded-full blur-[150px]" />
      </div>

      {/* Header - LOGO COMPLETELY UNCHANGED */}
      <header className="relative z-10">
        <nav className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* LOGO - LOCKED BRAND ASSET - DO NOT MODIFY */}
            <img src={LOGO_URL} alt="AIRA ECHO Logo" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="font-heading font-bold text-2xl">AIRA ECHO</h1>
              <p className="text-xs text-gray-500">AI-Powered Travel</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 text-white font-medium hover:shadow-lg hover:shadow-amber-500/25 transition-all" data-testid="explore-btn">
              Explore AIRA
            </Link>
          </div>
        </nav>
      </header>

      {/* NEW: Magazine-Style Travel Hero Section */}
      <TravelHeroSection />

      {/* Film Studio Section - Below Ask Anything, Above One Platform */}
      <FilmStudioSection />

      {/* Features Grid */}
      <section className="relative z-10 py-24 bg-black/20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-semibold mb-4">
              One Platform, <span className="glow-text">Infinite Possibilities</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              From daily tasks to life-changing decisions, AIRA is your intelligent companion.
            </p>
          </motion.div>

          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {features.map((feature, idx) => (
              <motion.div key={idx} variants={item}>
                <GlassCard className="p-6 h-full group" hover>
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-gray-400">{feature.desc}</p>
                </GlassCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <GlassCard className="p-12 glow-border">
              <Zap className="w-12 h-12 text-accent mx-auto mb-6" />
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Ready to Transform Your Life?
              </h2>
              <p className="text-gray-400 mb-8 max-w-xl mx-auto">
                Join thousands of users who are already using AIRA to manage their life, 
                business, and creativity more effectively.
              </p>
              <Link to="/signup" className="btn-primary inline-flex items-center gap-2" data-testid="cta-signup">
                Get Started Free
                <ArrowRight className="w-5 h-5" />
              </Link>
            </GlassCard>
          </motion.div>
        </div>
      </section>

      {/* Latest News Section */}
      <NewsSection />

      {/* Footer with Legal Links */}
      <Footer />
    </motion.div>
    </>
  );
}
